import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// The ways a report field says "no figure", and the rendering they share.
// Both cards need this knowledge -- each decides which state a row is in -- so
// it is stated here once rather than copied. EditableTextField no longer
// infers the state from the displayed string: it is TOLD, via `hasFigure`,
// because a refused currency figure displays "$0.00" and no amount of reading
// that string can tell it from a real one.

// Never 0 and never the formatter's output: both render as a figure, and the
// backend told us it has none.
export const UNAVAILABLE_DISPLAY = "N/A";

// The two states that exist BEFORE the backend has said anything at all, and
// the reason they are two rather than one.
//
// Every field on these cards used to be seeded with an invented figure -- a
// $10,000,000 starting value, a 4.5% return, 12 holdings -- so a report nobody
// had run yet was indistinguishable from one that had. A reader cannot tell an
// invented number from a calculated one, and on a client-facing report that is
// the worst failure available: the numbers were plausible, which is exactly
// what made them dangerous.
//
// A seed that says "no figure" fixes that, but ONE seed would answer two
// different questions with the same sentence. "Run the report" is true of a
// field the backend fills in; it is a lie about a field no backend code
// computes yet, and it sends the reader off to run a report that will change
// nothing. So the state names which of the two it is:
//
//   NO_REPORT       -- the backend DOES produce this figure; nothing has been
//                      run yet for this period. Running one replaces it.
//   NOT_IMPLEMENTED -- no backend calculation exists for this figure at all.
//                      Running a report will not change it, and the operator
//                      should know that rather than keep trying.
//
// Both are distinct from UNAVAILABLE_DISPLAY above, which is a THIRD thing and
// the only one that is an answer: the backend ran, reached this figure, and
// refused it -- with a reason it hands over, shown in the tooltip. N/A
// therefore outranks both of these wherever a reason is present.
export const NO_REPORT_DISPLAY = "No Report";
export const NOT_IMPLEMENTED_DISPLAY = "NOT IMPLEMENTED";

// A THIRD seed, and it answers a third question the other two cannot. The
// Harvest card's Shares Sold and Sellable Today are per-STOCK: the backend
// produced a figure for every Bucket 3 stock, so neither "run the report"
// nor "nothing computes this" is true of the empty row -- what is missing
// is the reader's own pick. Saying so is what tells them the field is
// waiting on the select between the two rows rather than on anything of
// ours, and it is the wording the operator asked for.
export const SELECT_STOCK_DISPLAY = "Select stock";

// Whether a stored report value is one of the two seeds above rather than a
// figure. Read off the STORED value, never off the formatted string: the
// formatters turn anything non-numeric into "$0.00" or "", so by the time a
// value has been formatted the distinction this asks about is already gone.
//
// Both cards need it and each decides its own row's state, so it is stated
// here once beside the strings it tests -- a private copy in either card is
// how one of them drifts the day a third state is added.
// Nullish is accepted and is simply not a placeholder: the cards' value union
// admits null (helpers/types carries why), and every caller here reaches this
// before it has decided which absence it is holding.
export const isPlaceholderDisplay = (
  value: number | string | null | undefined,
): boolean =>
  value === NO_REPORT_DISPLAY ||
  value === NOT_IMPLEMENTED_DISPLAY ||
  value === SELECT_STOCK_DISPLAY;

// A non-figure has to read as a state, not as a value, so it gives up the
// figure's weight and colour for muted, tracked-out text. Shared by all three
// -- N/A, No Report and NOT IMPLEMENTED.
//
// The Protect card's Available Margin Balance used to be a fourth case here:
// a 0 the UI hard-coded rather than calculated, kept as a number and marked
// so this treatment could tell it from a calculated one. It is not a number
// any more -- it defaults to no figure at all and so reads N/A like any other
// absence, which is what retired that flag (useAvailableMarginBalance carries
// why). The tracking still earns its keep on the same argument in reverse:
// the reason a placeholder had to be distinguished at all was that "$0.00" is
// a perfectly ordinary-looking figure, and a default that is a STATE cannot
// be misread as one in the first place.
//
// The reason is the same for each: none of them is a number the reader should
// take as an answer, and any of them left in full figure weight
// and navy looks exactly like one, and every real figure on these cards IS
// navy, green or red, and none of them are spaced. The mark stays
// typographic on purpose -- an underline is what would say "there is an
// explanation here", but the value is an <input>, browsers do not paint
// text-decoration on form controls, and a border would run the width of the
// field rather than the width of the word. The type size is left alone;
// shrinking it would change the field's height and pull the card out of line
// with its neighbours. On focus the muting lifts, because from then on the
// digits are the user's own and dressing them as unavailable would lie.
export const unavailableFieldSx = {
  display: "inline-flex",
  // The wrapper became a ROW when the info icon moved in beside the value:
  // centred so the icon sits on the figure's optical middle rather than its
  // baseline, and a half-step apart so the two do not touch. Both are inert
  // for the reasonless N/A below, which puts a single child in here.
  alignItems: "center",
  gap: 0.5,
  "& input": {
    color: "text.secondary",
    fontWeight: 400,
    letterSpacing: "0.08em",
  },
  "& input:focus": {
    color: "text.primary",
  },
};

// The wrapper for a row that has a real figure AND something to say about it
// -- a note: the backend produced this number, and it is qualified (a Bucket
// 3 figure measured from the bucket's creation date rather than the period's
// start). It is the same ROW as unavailableFieldSx -- inline-flex, centred so
// the icon sits on the figure's optical middle rather than its baseline, half
// a step apart so the two do not touch -- and nothing else.
//
// It cannot simply reuse unavailableFieldSx, and that is the whole reason
// this exists separately rather than as one shared object. That one MUTES the
// input: text.secondary, weight 400, 0.08em of tracking. On an N/A that is
// the point -- the two characters have to read as a state rather than as a
// value. On a real figure it would be a lie in the only vocabulary these
// cards have for it: every produced figure here is navy, green or red and
// none of them are spaced, so a note-bearing figure dressed that way would
// read as unavailable to anyone scanning the card, which is exactly the
// failure the muting above was introduced to prevent. A note qualifies a
// figure; it does not withdraw it.
export const notedFieldSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
};

// The one text a field's tooltip shows, assembled from every sentence that
// has something to say about the field, in the order given, blanks dropped.
//
// Two callers, one rule. The cards use it for the two CHANNELS a field has:
// `unavailableReason` (there is no figure, here is why) and `note` (there is
// a figure, here is what qualifies it). StrategyReport uses it a step
// earlier, to build one row's note out of more than one sentence -- the
// coverage line the frontend composes plus whatever the backend sent under
// `notes` for the same row. It is stated here once rather than copied into
// either place: a private join is how the two orders drift apart.
//
// The REASON goes first when both arrive. It is the more urgent of the two by
// a wide margin: it tells the reader the number in front of them is not a
// result, and a note read before that is a note about something the reader
// still thinks is a figure. A single space between them is deliberate -- both
// channels carry finished user-facing sentences, and the tooltip renders one
// text node, where a newline would not break anyway.
//
// Blank and whitespace-only inputs are dropped rather than joined: an icon
// that promises an explanation and opens an empty tooltip is worse than no
// icon at all, which is why the cards ask this for the whole text and render
// the icon only when it answers with something.
export const joinFieldStateText = (
  ...parts: (string | null | undefined)[]
): string | undefined => {
  const said = parts.filter((part): part is string => Boolean(part?.trim()));
  return said.length > 0 ? said.join(" ") : undefined;
};

// The seeds need their own sizing, and this is a legibility fix rather than a
// preference.
//
// N/A is two characters; "NOT IMPLEMENTED" is fifteen, and at the figure's
// 1.5rem with the tracking above it wants 249px of a card column that gives it
// 216px at 1280px wide and 190px at 480px. An <input> CLIPS rather than wraps,
// so at the figure's size it renders as "NOT IMPLEMENTE" -- a state nobody can
// read, which is the epic-#482 failure exactly: technically present, useless.
//
// Two things buy the width back. The tracking goes: 0.08em over fifteen
// characters spends ~29px making a two-letter N/A read as a state, and a
// capitalised phrase already reads as one without it. And the type drops to
// 1.05rem, which fits the longest seed into the narrowest column with room to
// spare.
//
// lineHeight is pinned in ABSOLUTE units, which is what keeps the constraint
// unavailableFieldSx records: the box height must not move, or the card falls
// out of line with its neighbours -- and it must not move on focus either,
// where the type size goes back up. 1.8rem is the figure row's own height
// (1.5rem x 1.2, EditableTextField).
export const placeholderFieldSx = {
  display: "inline-flex",
  alignItems: "center",
  minWidth: 0,
  "& input": {
    color: "text.secondary",
    fontWeight: 400,
    fontSize: "1.05rem",
    lineHeight: "1.8rem",
  },
  // From the moment it is focused the digits are the user's own figure, so it
  // gets the figure's type and colour back -- dressing what someone just typed
  // as "not implemented" would lie, the same reasoning as the muting above.
  "& input:focus": {
    color: "text.primary",
    fontSize: "1.5rem",
  },
};

// The reason has to be readable, not merely visible. MUI's `describeChild`
// hangs aria-describedby on the tooltip's wrapper, and a screen reader parked
// on the input inside never reaches it -- it hears "N/A" and no explanation.
// The cards render the reason into a node carrying this and point the input's
// own aria-describedby at it. Every length is an explicit unit: the sx system
// reads a bare `width: 1` as 100% and a bare `margin: -1` as a spacing step.
export const visuallyHiddenSx = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  border: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

// Why both cards wrap the value field the way they do, stated once because
// both do it identically and every clause of it is load-bearing:
//
// It covers a NOTE identically -- same wrapper, same hidden copy, same
// tooltip, with only the wrapper's sx differing (notedFieldSx above) -- so
// none of what follows is specific to a refusal.
//
// The tooltip wraps a plain DOM node rather than EditableTextField, because
// MUI clones its child to attach the open/close handlers and a ref, and a
// memo()'d function component accepts neither -- passed directly it would
// silently drop every handler. React's onFocus rides focusin, which bubbles,
// so focusing the input inside opens the tooltip; enterTouchDelay={0} gets it
// there on a first tap instead of a long press. describeChild keeps the reason
// a DESCRIPTION of the field rather than replacing its name. And the hidden
// copy inside the wrapper is what the input's own aria-describedby points at:
// the tooltip describes the wrapper, which a screen reader parked on the input
// never reads. The icon below rides INSIDE that wrapper, so it needs no
// handlers of its own: MUI's listeners sit on the wrapper and a pointer
// entering either child bubbles up to them, which is what keeps the value
// itself hoverable now that something sits beside it.

// The mark that says an explanation is here. N/A alone is mute -- it reports
// that a figure is missing and gives a reader no reason to think anything
// would answer why -- so the reason went undiscovered unless someone hovered
// a word they had no reason to hover. The icon is what makes the tooltip
// findable, and it is rendered ONLY where there is text to open: an icon
// promising an explanation and opening an empty tooltip is worse than no
// icon, which is why the cards render this inside the branch keyed off
// joinFieldStateText above rather than beside every N/A.
//
// It marks a NOTE on the same terms, and the name is now the narrower of the
// two things it does: a real figure carrying a note is exactly as mute as a
// bare N/A was -- nothing about a correct-looking number invites a reader to
// hover it -- so the mark is what makes the qualification findable at all.
// That is the whole reason the operator agreed to the coverage line leaving
// the card body (task 3144): the icon is what replaces the caption they
// could previously just read.
//
// It is aria-hidden and takes no tab stop DELIBERATELY. The reason already
// reaches assistive tech twice over -- the input's aria-describedby points at
// the hidden copy, and the tooltip describes the wrapper -- so an icon in the
// accessibility tree would announce a third copy of the same sentence and add
// a tab stop between the figures for no information. It is a pointer
// affordance; the keyboard path to the reason is focusing the field, which is
// unchanged.
//
// Sized in rem BELOW the figure's own 1.5rem so it reads as a mark against
// the value rather than a second glyph competing with it, and so it cannot
// grow the field's height and pull a card out of line with its neighbours --
// the constraint unavailableFieldSx above records for the type size. It never
// shrinks: at a narrow width the neighbour beside a row drops to its own line
// instead (MultiValuesCard says where), and an icon squeezed to a sliver
// would be a mark nobody could read.
const unavailableReasonIconSx = {
  fontSize: "1rem",
  color: "text.secondary",
  flexShrink: 0,
};

export const UnavailableReasonIcon = () => (
  <InfoOutlinedIcon aria-hidden sx={unavailableReasonIconSx} />
);
